import { IkeToken } from '@ikegps/core-api';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { MenuComponent } from '../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../components/menu/menu.item';
import { IMithrilComponent } from '../../components/mithril.component';
import { CSS as SpinnerCSS, Spinner } from '../../components/spinner/spinner';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services';
import { CurrentState } from '../../state/page.state';
import { CollectionAssignCtrl } from './collection.assign.ctrl';
import { CollectionDeleteCtrl } from './collection.delete.ctrl';
import { CollectionDuplicateCtrl } from './collection.duplicate.ctrl';
import { CollectionFormCtrl } from './collection.form.ctrl';
import { CollectionFormMigrateCtrl } from './collection.form.migrate.ctrl';
import { CollectionInsightProcessCtrl } from './collection.insight.process.ctrl';
import { CollectionMoveToDepartmentCtrl } from './collection.move.department.ctrl';
import { CollectionMoveToJobCtrl } from './collection.move.job.ctrl';
import { CollectionSetStatusCtrl } from './collection.status.ctrl';

export class CollectionEditMenu extends IMithrilComponent {
	private menu: MenuComponent;

	constructor() {
		super();

		this.menu = new MenuComponent({
			items: () => this.viewMenuItems(),
		});
	}

	get nodes(): MithrilVNode {
		if (!CurrentState.Tag.ready) {
			return Spinner.view(SpinnerCSS.buttonSize);
		}

		const currentUser = Services.UserProfile.get(Services.User.current.objectId);

		if (
			CurrentState.CollectionEdit.isDirty ||
			!this.menu.hasUsableItems ||
			Services.UserProfile.isDepartmentViewer(currentUser) ||
			Services.UserProfile.isDepartmentDownloader(currentUser)
		) {
			return ImageButton.view(MaterialIcon.MORE_VERT, { disabled: true });
		}

		return m('div', [ImageButton.view(MaterialIcon.MORE_VERT, { onclick: this.menu.show }), this.menu.view()]);
	}

	/**
	 * @todo Need to go over each of the visible function and add user role checks
	 *   EG: at time of writing the `canAssign` function can return true even if the user is a viewer or a downloader
	 */
	private viewMenuItems(): MenuItem[] {
		const currentUser = Services.UserProfile.get(Services.User.current.objectId);
		return [
			{
				visible: CollectionAssignCtrl.canAssign(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_ASSIGN), MaterialIcon.ASSIGN),
				action: () => CollectionAssignCtrl.showAssignModal(),
			},
			{
				visible: CollectionMoveToJobCtrl.canMove(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_MOVE_JOB), MaterialIcon.MOVE),
				action: () => CollectionMoveToJobCtrl.showMoveModal(),
			},
			{
				visible: CollectionMoveToDepartmentCtrl.canMove(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_MOVE_DEPARTMENT), MaterialIcon.EXIT_TO_APP),
				action: () => CollectionMoveToDepartmentCtrl.showMoveModal(),
			},
			{
				visible: CollectionDuplicateCtrl.canDuplicate(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_DUPLICATE), MaterialIcon.DUPLICATE),
				action: () => CollectionDuplicateCtrl.showDuplicateModal(),
			},
			{
				visible: CollectionSetStatusCtrl.canDisplay(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_STATUS), MaterialIcon.STATUS),
				action: () => CollectionSetStatusCtrl.showSetStatusModal(),
			},
			{
				visible: CollectionFormCtrl.canDisplay(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_UPDATE_FORM), MaterialIcon.FORM_UPDATE),
				action: () => CollectionFormCtrl.showUpdateFormModal(),
			},
			{
				visible: CollectionFormMigrateCtrl.canDisplay(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_MIGRATE_FORM), MaterialIcon.MOVE_DOWN),
				action: () => CollectionFormMigrateCtrl.showMigrateFormModal(),
			},
			{
				visible:
					(Services.UserProfile.isDepartmentAdmin(currentUser) ||
						Services.UserProfile.isDepartmentDataAdmin(currentUser)) &&
					Services.Token.has(IkeToken.FEATURE_REVISION_HISTORY) &&
					CurrentState.Tag.collectionSize === 1,
				view: MenuItemComponent.view(Translate(T.COLLECTION_REVISION_HISTORY), MaterialIcon.MANAGE_HISTORY),
				action: () =>
					Services.Route.go(IkePageName.collectionHistory, {
						collectionUuid: CurrentState.Tag.collections?.[0].uuid ?? '',
					}),
			},
			{
				visible: CollectionInsightProcessCtrl.canTriggerInsightProcessing(currentUser),
				view: MenuItemComponent.view(Translate(T.COLLECTION_INSIGHT_PROCESS), MaterialIcon.SPARKLE),
				action: () => CollectionInsightProcessCtrl.showInsightProcessModal(),
			},
			{
				visible: CollectionDeleteCtrl.canDisplay(),
				view: MenuItemComponent.view(Translate(T.COLLECTION_DELETE), MaterialIcon.DELETE),
				action: () => CollectionDeleteCtrl.showConfirmModal(),
			},
		];
	}
}
